import * as Api from "@modules/api";
import { useAppSelector } from "@lib/stores";
import {
  addEvent,
  clearNextContent,
  getNextContent,
  getSelectedPlayerContentEntityItemsById,
  setFirstPlayingChapterId,
  setForcedPlayingChapterId,
  setPlayingChapterId,
  setSelectedPlayerContent,
} from "@lib/stores/content";
import {
  Content,
  ItemContentOutputWithProgress,
} from "@lib/stores/content/types";
import { useGetNextContent } from "@lib/stores/content/hooks";
import { useB2B } from "../B2BContext";

export const useSelectedPlayerContent = () => {
  const nextTitleDisabled = useAppSelector(
    ({ auth }) => auth.user.nextTitleDisabled
  );

  const chapters = useAppSelector(
    ({ content }) => content.selectedPlayerContent.chapters.data
  );
  const isChaptersLoading =
    useAppSelector(
      ({ content }) => content.selectedPlayerContent.chapters.status
    ) === "loading";
  const isEnded = useAppSelector(
    ({ content }) => content.selectedPlayerContent.isPlayingChapterEnded
  );
  const selectedPlayerContent = useAppSelector(
    ({ content }) => content.selectedPlayerContent.content
  );
  const playingChapterId = useAppSelector(
    ({ content }) => content.selectedPlayerContent.playingChapterId
  );
  const firstPlayingChapterId = useAppSelector(
    ({ content }) => content.selectedPlayerContent.firstPlayingChapterId
  );
  const forcedPlayingChapterId = useAppSelector(
    ({ content }) => content.selectedPlayerContent.forcedPlayingChapterId
  );

  const { data: nextContentData } = useGetNextContent();

  const { b2B } = useB2B();

  const onPlayerContentSelect = (
    body: {
      content: Content;
      narratorId?: number | undefined;
      nextItemSource?: Api.Server.NextItemSource | undefined;
      playlistId?: number | undefined;
      isLocked?: boolean;
    },
    onSuccess?: (chapters: ItemContentOutputWithProgress[]) => void
  ) => {
    const { content, narratorId, isLocked } = body;

    setSelectedPlayerContent({
      id: content.id,
      title: content.title,
      author: content?.author,
      thumbnail: content.thumbnail,
      contentEntityType: content.contentEntityType,
      source: content.source,
    });

    getSelectedPlayerContentEntityItemsById(
      { contentEntityId: content.id, narratorId },
      onSuccess
    );

    if (!nextTitleDisabled && !isLocked && !b2B?.isB2B) {
      getNextContent({
        nextItemSource: body.nextItemSource || 0,
        type: content.contentEntityType,
        contentEntityId: content.id,
        playlistId: body.playlistId,
      });
    }
  };

  const onPlayingChapterSelect = (
    id: string | undefined,
    onSelect: () => void
  ) => {
    setPlayingChapterId(id);

    onSelect();
  };

  const onFirstPlayingChapterSelect = (id: string | undefined) => {
    setFirstPlayingChapterId(id);
  };

  const onForcedPlayingChapterSelect = (id: string | undefined) => {
    setForcedPlayingChapterId(id);
  };

  const onPlaying = (
    progress: number | undefined,
    playingMode?: Api.Server.PlayingMode
  ) => {
    if (playingChapterId) {
      addEvent({
        contentEntityId: playingChapterId,
        eventType: Api.Server.EventType.Playing,
        playingMode: playingMode || Api.Server.PlayingMode.MiniPlayer,
        currentProgress: progress,
      });
    }
  };

  const onSeek = (progress: number | undefined) => {
    if (playingChapterId) {
      addEvent({
        contentEntityId: playingChapterId,
        eventType: Api.Server.EventType.Seek,
        playingMode: Api.Server.PlayingMode.MiniPlayer,
        currentProgress: progress,
      });
    }
  };

  const onNextChapterStart = (
    id: string | undefined,
    playingMode: Api.Server.PlayingMode,
    progress: number | undefined
  ) => {
    if (id) {
      addEvent({
        contentEntityId: id,
        eventType: Api.Server.EventType.NextContent,
        playingMode,
        currentProgress: progress,
      });
    }
  };

  const onPause = (progress: number | undefined) => {
    if (playingChapterId) {
      addEvent({
        contentEntityId: playingChapterId,
        eventType: Api.Server.EventType.PausePlaying,
        playingMode: Api.Server.PlayingMode.MiniPlayer,
        currentProgress: progress,
      });
    }
  };

  const onStop = (progress: number | undefined) => {
    if (playingChapterId) {
      addEvent({
        contentEntityId: playingChapterId,
        eventType: Api.Server.EventType.StopPlaying,
        playingMode: Api.Server.PlayingMode.MiniPlayer,
        currentProgress: progress,
      });
    }
  };

  const onStartPlaying = (progress: number | undefined) => {
    if (playingChapterId) {
      addEvent({
        contentEntityId: playingChapterId,
        eventType: Api.Server.EventType.StartPlaying,
        playingMode: Api.Server.PlayingMode.MiniPlayer,
        currentProgress: progress,
      });
    }
  };

  const onNextContentGet = (
    onSuccess: () => void,
    isLocked: boolean | undefined
  ) => {
    onSuccess();

    if (isLocked) return;

    clearNextContent();
  };

  return {
    chapters,
    isLoading: isChaptersLoading,
    isEnded,
    selectedPlayerContent,
    playingChapterId,
    firstPlayingChapterId,
    forcedPlayingChapterId,
    onPlayerContentSelect,
    onPlayingChapterSelect,
    onFirstPlayingChapterSelect,
    onForcedPlayingChapterSelect,
    onPlaying,
    onSeek,
    onNextChapterStart,
    onPause,
    onStop,
    onStartPlaying,
    setSelectedPlayerContent,
    getSelectedPlayerContentEntityItemsById,
    onNextContentGet,
    nextContent: {
      content: nextContentData,
    },
  };
};
